// components/InstallApp.jsx
import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide
} from '@mui/material';
import {
  InstallMobile as InstallIcon,
  Close as CloseIcon,
  GetApp as GetAppIcon,
  Android as AndroidIcon,
  Apple as AppleIcon
} from '@mui/icons-material';

const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

const InstallApp = ({ language }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed (standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install banner only if not already installed
      if (!isInStandaloneMode) {
        // Check if user dismissed the banner before
        const hasSeenInstallPrompt = localStorage.getItem('install_prompt_dismissed');
        if (!hasSeenInstallPrompt) {
          setShowInstallBanner(true);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show custom install instructions
    if (isIOSDevice && !isInStandaloneMode) {
      const hasSeenIOSPrompt = localStorage.getItem('ios_install_prompt_dismissed');
      if (!hasSeenIOSPrompt) {
        setShowInstallDialog(true);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // For iOS or if no deferred prompt
      showIOSInstructions();
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for user's choice
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowInstallBanner(false);
      setShowInstallDialog(false);
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the deferred prompt
    setDeferredPrompt(null);
  };

  const showIOSInstructions = () => {
    setShowInstallDialog(true);
  };

  const handleDismissBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('install_prompt_dismissed', 'true');
  };

  const handleDismissIOSDialog = () => {
    setShowInstallDialog(false);
    localStorage.setItem('ios_install_prompt_dismissed', 'true');
  };

  // Don't show anything if already installed
  if (isStandalone) return null;

  return (
    <>
      {/* Install Banner */}
      <Snackbar
        open={showInstallBanner}
        autoHideDuration={10000}
        onClose={handleDismissBanner}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: '1px solid #FFD700',
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              color: '#FFD700'
            }
          }}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={handleInstall}
                sx={{
                  color: '#FFD700',
                  fontWeight: 600
                }}
              >
                {language === 'ar' ? 'تثبيت' : 'Install'}
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleDismissBanner}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          {language === 'ar' 
            ? '📱 قم بتثبيت التطبيق على جهازك للوصول السريع!'
            : '📱 Install our app on your device for quick access!'}
        </Alert>
      </Snackbar>

      {/* Install Dialog (especially for iOS) */}
      <Dialog
        open={showInstallDialog}
        onClose={handleDismissIOSDialog}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '20px',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GetAppIcon />
            {language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {isIOS ? (
            <Box>
              <Typography sx={{ color: '#FFFFFF', mb: 2 }}>
                {language === 'ar' 
                  ? 'لتثبيت التطبيق على جهاز iOS:'
                  : 'To install the app on iOS:'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#1A1A1A',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFD700'
                  }}>
                    1
                  </Box>
                  <Typography sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' 
                      ? 'اضغط على زر المشاركة 📤'
                      : 'Tap the share button 📤'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#1A1A1A',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFD700'
                  }}>
                    2
                  </Box>
                  <Typography sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' 
                      ? 'اختر "إضافة إلى الشاشة الرئيسية"'
                      : 'Select "Add to Home Screen"'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#1A1A1A',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFD700'
                  }}>
                    3
                  </Box>
                  <Typography sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' 
                      ? 'انقر على "إضافة"'
                      : 'Tap "Add"'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ color: '#FFFFFF', mb: 2 }}>
                {language === 'ar' 
                  ? 'انقر على زر التثبيت أدناه لإضافة التطبيق إلى جهازك'
                  : 'Click the install button below to add the app to your device'}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={handleInstall}
                startIcon={<AndroidIcon />}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  fontWeight: 700,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#FFE55C'
                  }
                }}
              >
                {language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={handleDismissIOSDialog} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'تذكر لاحقاً' : 'Remind Later'}
          </Button>
          {isIOS && (
            <Button
              onClick={handleDismissIOSDialog}
              sx={{ color: '#FFD700' }}
            >
              {language === 'ar' ? 'فهمت' : 'Got it'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallApp;